// Screen 2: Paper Discovery / Explore — search, filter, triage.
// Layout: sidebar | (topbar + filter rail | results | preview rail)

import { searchArxiv, importPapers, ARXIV_DAY_PRESETS } from '../src/lib/arxiv.js';
import { useAuth } from '../src/lib/auth.jsx';
import { usePapers, invalidatePapersCache } from '../src/lib/use-papers.js';

function ExploreScreen({ route }) {
  const { t, lang } = useT();
  const auth = useAuth();
  const navCtx = React.useContext(NavContext);
  const { papers, status: papersStatus, refresh: refreshPapers } = usePapers();
  const isLibrary = route === 'library';

  const [query, setQuery]       = React.useState('');
  const [days, setDays]         = React.useState(30);
  const [results, setResults]   = React.useState(null);          // null = 미검색, []는 빈 결과
  const [loading, setLoading]   = React.useState(false);
  const [error, setError]       = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  const [importStatus, setImportStatus] = React.useState(null);  // {arxiv_id: 'added'|'dupe'|'failed'}
  const [selected, setSelected] = React.useState(new Set());
  const previewRef = React.useRef(null);
  const [preview, setPreview]   = React.useState(null);

  const handleSearch = React.useCallback(async (q = query, d = days) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    setImportStatus({});
    // One automatic retry on rate_limited — arXiv's throttle is bursty and a
    // ~1.2s wait almost always clears it. Beyond that, surface the error so
    // the user can hit Search again themselves.
    const attempt = async (left) => {
      try {
        const data = await searchArxiv({ q: trimmed, days: d });
        setResults(data.items ?? []);
        setPreview(data.items?.[0] ?? null);
        setSource(data.source ?? null);
      } catch (e) {
        if (left > 0 && (e.code === 'rate_limited' || e.status === 429 || e.status === 503)) {
          await new Promise((r) => setTimeout(r, 1200));
          return attempt(left - 1);
        }
        throw e;
      }
    };
    try {
      await attempt(1);
    } catch (e) {
      setError(e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query, days]);

  const [source, setSource] = React.useState(null);

  function toggleSelect(arxivId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(arxivId)) next.delete(arxivId); else next.add(arxivId);
      return next;
    });
  }

  async function handleImport() {
    if (!auth.user) {
      auth.signIn();
      return;
    }
    if (selected.size === 0 || !results) return;
    const items = results.filter((r) => selected.has(r.arxiv_id));
    setImporting(true);
    try {
      const data = await importPapers(items);
      const next = {};
      for (const it of items) {
        next[it.arxiv_id] = data.inserted > 0 ? 'added' : 'dupe';
      }
      setImportStatus((s) => ({ ...s, ...next }));
      setSelected(new Set());
      // Bust the cached library so the Library/Dashboard/Projects screens
      // (and the right-rail preview when this user goes back to browse mode)
      // pick up the new rows on next render.
      invalidatePapersCache();
      refreshPapers();
    } catch (e) {
      setError(e);
    } finally {
      setImporting(false);
    }
  }

  // Library mode → render LibraryView. Explore mode keeps its arXiv search
  // workflow below.
  if (isLibrary) {
    return <LibraryView
      papers={papers}
      papersStatus={papersStatus}
      auth={auth}
      lang={lang}
      navCtx={navCtx}
    />;
  }

  return (
    <div className="pp pp-screen" data-screen-label="02 Explore">
      <Sidebar active="explore"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.workspace'), t('crumb.explore')]}/>}
          search="dynamic gaussian splatting · deformation cache · real-time"
        />

        <div className="pp-content">
          {/* LEFT filter rail */}
          <RailLeft id="explore-filters" defaultWidth={260} min={200} max={380} className="pp-filter-rail scroll">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div className="section-title"><span className="num">{t('exp.filters')}</span></div>
              <button className="btn ghost sm" style={{ height: 22, padding: '0 6px' }}>{t('exp.reset')}</button>
            </div>

            <FilterGroup title={t('exp.qmode')}>
              <SegControl options={[t('exp.qmode.semantic'), t('exp.qmode.keyword'), t('exp.qmode.hybrid')]} active={t('exp.qmode.semantic')}/>
            </FilterGroup>

            <FilterGroup title={t('exp.date')} badge="14d">
              <DatePills/>
            </FilterGroup>

            <FilterGroup title={t('exp.topic')} badge={t('exp.topic.selected')}>
              <CheckRow label="4DGS"             count="42" on/>
              <CheckRow label="3DGS"             count="61" on/>
              <CheckRow label="NeRF"             count="28"/>
              <CheckRow label="SfM"              count="14"/>
              <CheckRow label="VLA"              count="33" on/>
              <CheckRow label="Robot foundation" count="27" on/>
              <CheckRow label="Multimodal"       count="19"/>
              <CheckRow label="LM efficiency"    count="11"/>
            </FilterGroup>

            <FilterGroup title={t('exp.venue')}>
              <CheckRow label="CVPR / ICCV / ECCV" count="38" on/>
              <CheckRow label="NeurIPS / ICLR / ICML" count="22"/>
              <CheckRow label="CoRL / RSS / ICRA" count="29" on/>
              <CheckRow label="SIGGRAPH" count="6"/>
              <CheckRow label="arXiv only" count="33"/>
            </FilterGroup>

            <FilterGroup title={t('exp.method')}>
              <CheckRow label="diffusion prior"      count="14"/>
              <CheckRow label="deformation field"    count="9" on/>
              <CheckRow label="sparse-view"          count="11"/>
              <CheckRow label="mixture-of-experts"   count="7"/>
            </FilterGroup>

            <FilterGroup title={t('exp.dataset')}>
              <CheckRow label="D-NeRF"     count="22" on/>
              <CheckRow label="HyperNeRF"  count="14"/>
              <CheckRow label="DL3DV-10K"  count="9"/>
              <CheckRow label="ALOHA"      count="11"/>
              <CheckRow label="Bridge-V3"  count="8"/>
              <CheckRow label="Open-X"     count="6"/>
            </FilterGroup>

            <FilterGroup title={t('exp.signals')}>
              <ToggleRow label={t('exp.signal.os')}   on/>
              <ToggleRow label={t('exp.signal.real')}/>
              <ToggleRow label={t('exp.signal.bookmark')}/>
              <ToggleRow label={t('exp.signal.unread')} on/>
              <ToggleRow label={t('exp.signal.inproj')}/>
            </FilterGroup>

            <FilterGroup title={t('exp.scores')}>
              <div className="col gap-3" style={{ padding: '6px 8px' }}>
                <ScoreSlider label={t('exp.score.worth')} min={62}/>
                <ScoreSlider label={t('exp.score.novel')} min={40}/>
                <ScoreSlider label={t('exp.score.impl')} min={0}/>
              </div>
            </FilterGroup>
          </RailLeft>

          {/* CENTER results */}
          <div className="pp-explore-center scroll">
            {/* arXiv search bar */}
            <div className="pp-arxiv-searchbar">
              <div className="pp-arxiv-input-wrap">
                <I name="search" size={14}/>
                <input
                  className="pp-arxiv-input"
                  placeholder={lang === 'ko' ? 'arXiv 검색 (예: 4DGS, dynamic gaussian splatting…)' : 'Search arXiv (e.g. 4DGS, dynamic gaussian splatting…)'}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                />
                {loading && <span className="pp-arxiv-spinner" aria-hidden/>}
              </div>
              <select
                className="pp-arxiv-days"
                value={days ?? ''}
                onChange={(e) => {
                  // Don't auto-trigger another search — rapid select changes
                  // were causing per-IP rate-limits. User hits Search when
                  // ready.
                  const v = e.target.value;
                  setDays(v === '' ? null : Number(v));
                }}
              >
                {ARXIV_DAY_PRESETS.map((p) => (
                  <option key={String(p.id)} value={p.id ?? ''}>{lang === 'ko' ? p.labelKo : p.labelEn}</option>
                ))}
              </select>
              <button
                className="btn primary sm"
                onClick={() => handleSearch()}
                disabled={loading || !query.trim()}
              >
                <I name="search" size={12}/> {lang === 'ko' ? '검색' : 'Search'}
              </button>
              {results !== null && selected.size > 0 && (
                <button
                  className="btn primary sm"
                  onClick={handleImport}
                  disabled={importing}
                  style={{ marginLeft: 8 }}
                >
                  <I name="plus" size={12}/>{' '}
                  {lang === 'ko'
                    ? (auth.user ? `라이브러리에 추가 (${selected.size})` : `로그인 후 추가 (${selected.size})`)
                    : (auth.user ? `Add to library (${selected.size})` : `Sign in to add (${selected.size})`)}
                </button>
              )}
            </div>

            {/* result header */}
            <div className="pp-explore-header">
              <div className="col gap-1">
                <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                  <span className="serif" style={{ fontSize: 22, color: 'var(--fg-0)' }}>
                    {results === null ? t('exp.results') : (lang === 'ko' ? 'arXiv 검색 결과' : 'arXiv search results')}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                    {results === null
                      ? t('exp.results.meta', '0.18s')
                      : (loading
                          ? (lang === 'ko' ? '검색 중…' : 'searching…')
                          : `${results.length} ${lang === 'ko' ? '편' : 'items'}`)}
                  </span>
                  {results !== null && source && source !== 'arxiv' && (
                    <span className="chip" style={{ height: 18, fontSize: 10 }} title="arXiv에 결과가 없거나 throttle되어 백업 검색 엔진으로 fallback했습니다">
                      via {source.startsWith('openalex') ? 'OpenAlex' : 'Semantic Scholar'}
                    </span>
                  )}
                </div>
                {results === null && (
                  <div className="row gap-2">
                    <span className="chip accent">4DGS</span>
                    <span className="chip accent">3DGS</span>
                    <span className="chip accent">VLA</span>
                    <span className="chip accent">Robot foundation</span>
                    <span className="chip"><I name="link" size={10}/> deformation field</span>
                    <span className="chip"><I name="link" size={10}/> D-NeRF</span>
                    <span className="chip ok"><I name="github" size={10}/> open-source</span>
                    <span className="chip"><I name="clock" size={10}/> ≤ 14 days</span>
                  </div>
                )}
                {error && (
                  <div style={{ color: 'var(--warn)', fontSize: 12 }}>{error.message}</div>
                )}
              </div>
            </div>

            {/* results grid */}
            <div className="pp-explore-grid">
              {results === null
                ? PAPERS.slice(0, 6).map(p => <ExplorePaperCard key={p.id} paper={p} selected={p.id === 'p4'}/>)
                : results.map((r) => (
                    <ArxivResultCard
                      key={r.arxiv_id}
                      item={r}
                      selected={selected.has(r.arxiv_id)}
                      status={importStatus?.[r.arxiv_id]}
                      onToggle={() => toggleSelect(r.arxiv_id)}
                      onPreview={() => setPreview(r)}
                    />
                  ))}
            </div>

            {results !== null && results.length === 0 && !loading && (
              <div style={{ padding: 24, color: 'var(--fg-2)', fontSize: 13, textAlign: 'center' }}>
                {lang === 'ko' ? '검색 결과가 없습니다. 키워드/기간을 바꿔 보세요.' : 'No results. Try a different keyword or window.'}
              </div>
            )}
          </div>

          {/* RIGHT preview */}
          <RailRight id="explore-preview" defaultWidth={360} min={280} max={500} className="pp-preview-rail scroll">
            {results === null
              ? (papers[3] || papers[0]) && <PreviewPanel paper={papers[3] ?? papers[0]}/>
              : <ArxivPreview item={preview} lang={lang}/>}
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function LibraryView({ papers, papersStatus, auth, lang, navCtx }) {
  const { t } = useT();
  const [query, setQuery] = React.useState('');
  const owned = papers.filter((p) => p?._db);
  const q = query.trim().toLowerCase();
  const filtered = !q ? owned : owned.filter((p) => {
    const hay = [
      p.title, p.summary, p.venue, p.arxiv,
      (p.authors || []).join(' '),
      (p.tags || []).join(' '),
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="pp pp-screen" data-screen-label="02 Library">
      <Sidebar active="library"/>
      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.workspace'), lang === 'ko' ? '내 서재' : 'Library']}/>}
          search={lang === 'ko' ? '내 서재에서 검색…' : 'Search my library…'}
        />
        <div className="pp-content" style={{ display: 'block', overflow: 'auto' }}>
          <div style={{ padding: '24px 32px' }}>
            <div className="row gap-2" style={{ alignItems: 'baseline', marginBottom: 16, flexWrap: 'wrap' }}>
              <span className="serif" style={{ fontSize: 24 }}>{lang === 'ko' ? '내 서재' : 'Library'}</span>
              <span className="chip accent" style={{ height: 20 }}>{owned.length}</span>
              {q && (
                <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                  {filtered.length}/{owned.length} {lang === 'ko' ? '일치' : 'match'}
                </span>
              )}
              <span style={{ fontSize: 12, color: 'var(--fg-3)', marginLeft: 'auto' }}>
                {papersStatus === 'loading' ? (lang === 'ko' ? '불러오는 중…' : 'loading…') : ''}
              </span>
              <button className="btn sm primary" onClick={() => navCtx.navigate?.('explore')}>
                <I name="search" size={12}/> {lang === 'ko' ? 'arXiv에서 추가' : 'Add from arXiv'}
              </button>
            </div>

            {auth.user && owned.length > 0 && (
              <div className="pp-arxiv-searchbar" style={{ padding: 0, marginBottom: 16 }}>
                <div className="pp-arxiv-input-wrap">
                  <I name="search" size={14}/>
                  <input
                    className="pp-arxiv-input"
                    placeholder={lang === 'ko' ? '제목 · 저자 · 초록 · 태그…' : 'Title, author, abstract, tag…'}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                  />
                  {query && (
                    <button className="iconbtn" onClick={() => setQuery('')} title={lang === 'ko' ? '지우기' : 'Clear'}>
                      <I name="close" size={12}/>
                    </button>
                  )}
                </div>
              </div>
            )}

            {!auth.user ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-2)', fontSize: 13 }}>
                {lang === 'ko' ? '로그인하면 본인의 라이브러리가 여기에 표시됩니다.' : 'Sign in to see your library.'}
                <div style={{ marginTop: 12 }}>
                  <button className="btn sm primary" onClick={() => auth.signIn()}>
                    {lang === 'ko' ? 'Google로 로그인' : 'Sign in with Google'}
                  </button>
                </div>
              </div>
            ) : owned.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-2)', fontSize: 13 }}>
                {lang === 'ko'
                  ? '아직 추가된 논문이 없습니다. 탐색 화면에서 검색해 라이브러리에 추가해 보세요.'
                  : 'Library is empty. Add papers from the Explore screen.'}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-2)', fontSize: 13 }}>
                {lang === 'ko' ? `"${query}" 에 일치하는 논문이 없습니다.` : `No papers match "${query}".`}
              </div>
            ) : (
              <div className="pp-explore-grid">
                {filtered.map((p) => <LibraryPaperCard key={p.id} paper={p} lang={lang}/>)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LibraryPaperCard({ paper, lang }) {
  const navCtx = React.useContext(NavContext);
  const meta = paper._meta ?? {};
  const arxivId = paper.arxiv || meta.arxiv_id;
  const date = paper.date || (meta.published ? meta.published.slice(0, 10) : '');
  return (
    <div className="pp-explore-card pp-arxiv-card"
         onClick={() => navCtx.navigate?.('detail', { id: paper.id })}
         style={{ cursor: 'pointer' }}>
      <div className="pp-arxiv-card-head">
        <div className="col grow" style={{ minWidth: 0 }}>
          <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            {paper.venue && <span className="chip accent" style={{ height: 18 }}>{paper.venue}</span>}
            {arxivId && <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{date} · arXiv {arxivId}</span>}
            {paper.status && <span className="chip" style={{ height: 18, fontSize: 10 }}>{paper.status}</span>}
          </div>
          <div className="serif" style={{ fontSize: 15, lineHeight: 1.3, marginTop: 4, color: 'var(--fg-0)' }}>
            {paper.title}
          </div>
          {paper.authors?.length > 0 && (
            <div className="ellipsis" style={{ fontSize: 11.5, color: 'var(--fg-2)', marginTop: 4 }}>
              {paper.authors.slice(0, 5).join(', ')}{paper.authors.length > 5 ? ', et al.' : ''}
            </div>
          )}
        </div>
      </div>
      {paper.summary && (
        <div className="pp-arxiv-summary">{paper.summary.slice(0, 280)}{paper.summary.length > 280 ? '…' : ''}</div>
      )}
      <div className="row gap-2" style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
        {meta.pdf_url && (
          <a className="btn sm ghost" href={meta.pdf_url} target="_blank" rel="noreferrer">
            <I name="external" size={11}/> PDF
          </a>
        )}
        {(meta.html_url || (arxivId && `https://arxiv.org/abs/${arxivId}`)) && (
          <a className="btn sm ghost" href={meta.html_url ?? `https://arxiv.org/abs/${arxivId}`} target="_blank" rel="noreferrer">
            <I name="external" size={11}/> abs
          </a>
        )}
      </div>
    </div>
  );
}

function ArxivResultCard({ item, selected, status, onToggle, onPreview }) {
  const authors = (item.authors ?? []).slice(0, 5).join(', ') + ((item.authors ?? []).length > 5 ? ', et al.' : '');
  const date = item.published ? item.published.slice(0, 10) : '';
  // Card body opens the preview pane. The checkbox area calls stopPropagation
  // on both onClick and onMouseDown so toggling selection never also fires
  // the card-level preview handler.
  return (
    <div className={'pp-explore-card pp-arxiv-card' + (selected ? ' is-selected' : '') + (status === 'added' ? ' is-added' : '')}
         onClick={onPreview}>
      <div className="pp-arxiv-card-head">
        <label
          className="pp-arxiv-check"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            onClick={(e) => e.stopPropagation()}
          />
        </label>
        <div className="col grow" style={{ minWidth: 0 }}>
          <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            {item.primary_category && <span className="chip accent" style={{ height: 18 }}>{item.primary_category}</span>}
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{date} · arXiv {item.arxiv_id}</span>
            {status === 'added' && <span className="chip ok" style={{ height: 18 }}>added</span>}
            {status === 'dupe' && <span className="chip" style={{ height: 18 }}>already in library</span>}
          </div>
          <div className="serif" style={{ fontSize: 15, lineHeight: 1.3, marginTop: 4, color: 'var(--fg-0)' }}>
            {item.title}
          </div>
          <div className="ellipsis" style={{ fontSize: 11.5, color: 'var(--fg-2)', marginTop: 4 }}>{authors}</div>
        </div>
      </div>
      <div className="pp-arxiv-summary">{(item.summary ?? '').slice(0, 280)}{item.summary?.length > 280 ? '…' : ''}</div>
      <div className="row gap-2" style={{ marginTop: 8 }}>
        {item.pdf_url && (
          <a className="btn sm ghost" href={item.pdf_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
            <I name="external" size={11}/> PDF
          </a>
        )}
        {item.html_url && (
          <a className="btn sm ghost" href={item.html_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
            <I name="external" size={11}/> abs
          </a>
        )}
      </div>
    </div>
  );
}

function ArxivPreview({ item, lang }) {
  if (!item) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 12 }}>
        {lang === 'ko' ? '왼쪽 결과를 클릭하면 미리보기가 표시됩니다.' : 'Click a result to preview.'}
      </div>
    );
  }
  return (
    <div className="col gap-2" style={{ padding: 16 }}>
      <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
        {(item.categories ?? []).slice(0, 4).map((c) => (
          <span key={c} className="chip" style={{ height: 18 }}>{c}</span>
        ))}
      </div>
      <div className="serif" style={{ fontSize: 17, lineHeight: 1.3, color: 'var(--fg-0)' }}>{item.title}</div>
      <div style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>{(item.authors ?? []).join(', ')}</div>
      <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>
        {item.published?.slice(0, 10)} · arXiv {item.arxiv_id}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--fg-1)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{item.summary}</p>
      <div className="row gap-2">
        {item.pdf_url && <a className="btn sm" href={item.pdf_url} target="_blank" rel="noreferrer"><I name="external" size={11}/> PDF</a>}
        {item.html_url && <a className="btn sm ghost" href={item.html_url} target="_blank" rel="noreferrer"><I name="external" size={11}/> arXiv abs</a>}
      </div>
    </div>
  );
}

function DatePills() {
  const [v, setV] = React.useState('14d');
  return (
    <div className="row gap-1">
      {['24h','7d','14d','30d','90d','all'].map(p => (
        <button key={p} className={'pp-pill ' + (p === v ? 'is-on' : '')} onClick={() => setV(p)}>{p}</button>
      ))}
    </div>
  );
}

function FilterGroup({ title, badge, children }) {
  return (
    <div className="pp-filter-group">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', padding: '0 4px 6px' }}>
        <div style={{ fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{title}</div>
        {badge && <span className="mono" style={{ fontSize: 10, color: 'var(--acc-indigo)' }}>{badge}</span>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SegControl({ options, active }) {
  const [v, setV] = React.useState(active);
  return (
    <div className="pp-seg">
      {options.map(o => (
        <button key={o} className={'pp-seg-btn ' + (o === v ? 'is-on' : '')} onClick={() => setV(o)}>{o}</button>
      ))}
    </div>
  );
}

function CheckRow({ label, count, on }) {
  const [checked, setChecked] = React.useState(!!on);
  return (
    <div className={'pp-check-row ' + (checked ? 'is-on' : '')} onClick={() => setChecked(v => !v)}>
      <div className="pp-check-box">{checked && <I name="check" size={10}/>}</div>
      <span className="grow ellipsis">{label}</span>
      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{count}</span>
    </div>
  );
}

function ToggleRow({ label, on }) {
  const [checked, setChecked] = React.useState(!!on);
  return (
    <div className="pp-toggle-row" onClick={() => setChecked(v => !v)} style={{ cursor: 'pointer' }}>
      <span className="grow">{label}</span>
      <div className={'pp-toggle ' + (checked ? 'is-on' : '')}>
        <div className="pp-toggle-knob"/>
      </div>
    </div>
  );
}

function ScoreSlider({ label, min }) {
  return (
    <div className="col gap-2">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, color: 'var(--fg-1)' }}>{label}</span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--acc-indigo)' }}>≥ {min}</span>
      </div>
      <div className="pp-slider-track">
        <div className="pp-slider-fill" style={{ left: `${min}%`, right: '0%' }}/>
        <div className="pp-slider-knob" style={{ left: `${min}%` }}/>
      </div>
    </div>
  );
}

function ExplorePaperCard({ paper, selected }) {
  const nav = React.useContext(NavContext);
  return (
    <div className={'pp-explore-card ' + (selected ? 'is-selected' : '')}
      onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)}>
      <div className="row" style={{ position: 'absolute', top: 12, right: 12, gap: 4 }}>
        <button className="iconbtn" title="Compare">
          <div className="pp-compare-check">{selected && <I name="check" size={10}/>}</div>
        </button>
      </div>

      <div className="row gap-2" style={{ alignItems: 'baseline' }}>
        <span className="chip accent">{paper.venue}</span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{paper.date}</span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>arXiv {paper.arxiv}</span>
      </div>

      <div className="serif" style={{ fontSize: 16.5, lineHeight: 1.2, marginTop: 6, paddingRight: 30 }}>{paper.title}</div>

      <div className="row gap-2" style={{ marginTop: 6 }}>
        <Avatars names={paper.authors} size={15}/>
        <span style={{ fontSize: 11.5, color: 'var(--fg-2)' }} className="ellipsis">
          {paper.authors.slice(0, 3).join(', ')} +{Math.max(0, paper.authors.length - 3)}
        </span>
      </div>

      <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.5, marginTop: 8 }}>{paper.summary}</div>

      <div className="row gap-2" style={{ flexWrap: 'wrap', marginTop: 8 }}>
        {paper.tags.map(t => <span key={t} className="tag indigo">{t}</span>)}
        {paper.dataset && <span className="tag">{paper.dataset}</span>}
        {paper.code === 'released' && <span className="chip ok"><I name="github" size={10}/> code</span>}
      </div>

      {paper.why && (
        <div className="pp-explore-why">
          <I name="sparkles" size={11} style={{ color: 'var(--acc-violet)' }}/>
          <span style={{ fontSize: 11.5, color: 'var(--fg-1)' }}>{paper.why}</span>
        </div>
      )}

      <div className="row" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border-1)', justifyContent: 'space-between' }}>
        <div className="row gap-3">
          <ScoreInline label="WORTH" value={paper.worth} color="var(--acc-indigo)"/>
          <ScoreInline label="NOVEL" value={paper.novel} color="var(--acc-violet)"/>
          <ScoreInline label="IMPL"  value={paper.impl}  color="var(--acc-cyan)"/>
        </div>
        <div className="row gap-1" onClick={e => e.stopPropagation()}>
          <button className="iconbtn"><I name="bookmark" size={14}/></button>
          <button className="iconbtn"><I name="plus" size={14}/></button>
          <button className="iconbtn" onClick={() => nav.navigate && nav.navigate('reader')}><I name="external" size={14}/></button>
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ paper }) {
  const { t } = useT();
  const nav = React.useContext(NavContext);
  return (
    <div className="col gap-4">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div className="section-title"><span className="num">{t('exp.preview')}</span></div>
        <RailCollapseBtn/>
      </div>

      <div className="col gap-2">
        <div className="row gap-2">
          <span className="chip accent">{paper.venue}</span>
          <span className="chip violet"><I name="flame" size={10}/> top 3%</span>
        </div>
        <div className="serif" style={{ fontSize: 19, lineHeight: 1.18, letterSpacing: '-0.012em' }}>{paper.title}</div>
        <div style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>{paper.authors.join(', ')}</div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="section-title" style={{ marginBottom: 8 }}>{t('exp.preview.glance')}</div>
        <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.55 }}>{paper.summary}</div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="section-title" style={{ marginBottom: 10 }}>{t('exp.preview.worth')}</div>
        <div className="col gap-3">
          <EvalLine label={t('exp.eval.relevance')} value={0.94} color="var(--acc-indigo)"/>
          <EvalLine label={t('exp.eval.novelty')}    value={0.86} color="var(--acc-violet)"/>
          <EvalLine label={t('exp.eval.impl')}       value={0.71} color="var(--acc-cyan)"/>
          <EvalLine label={t('exp.eval.signif')}     value={0.78} color="var(--ok)"/>
          <EvalLine label={t('exp.eval.diff')}       value={0.65} color="var(--warn)"/>
        </div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="section-title" style={{ marginBottom: 8 }}>{t('exp.preview.connect')}</div>
        <div className="col gap-2">
          <ConnectLine project="Server-rack digital twin" reason="thin-geometry handling, 120 Hz target"/>
          <ConnectLine project="4D reconstruction" reason="deformation field thread from wiki"/>
        </div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        <div className="section-title" style={{ marginBottom: 8 }}>{t('exp.preview.bench')}</div>
        <div className="pp-mini-bench">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="col" style={{ gap: 2 }}>
              <span style={{ fontSize: 10.5, color: 'var(--fg-3)' }} className="mono">D-NeRF · PSNR</span>
              <span className="serif" style={{ fontSize: 22, color: 'var(--acc-indigo)' }}>31.74</span>
              <span style={{ fontSize: 10, color: 'var(--ok)' }} className="mono">+1.32 {t('exp.preview.bench.vs')}</span>
            </div>
            <div className="col" style={{ gap: 2 }}>
              <span style={{ fontSize: 10.5, color: 'var(--fg-3)' }} className="mono">FPS</span>
              <span className="serif" style={{ fontSize: 22, color: 'var(--acc-violet)' }}>84</span>
              <span style={{ fontSize: 10, color: 'var(--ok)' }} className="mono">3.1{t('exp.preview.bench.speedup')}</span>
            </div>
            <div className="col" style={{ gap: 2 }}>
              <span style={{ fontSize: 10.5, color: 'var(--fg-3)' }} className="mono">LPIPS</span>
              <span className="serif" style={{ fontSize: 22, color: 'var(--acc-cyan)' }}>.041</span>
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }} className="mono">−0.012</span>
            </div>
          </div>
        </div>
      </div>

      <div className="row gap-2">
        <button className="btn primary grow" onClick={() => nav.navigate && nav.navigate('detail', paper?.id ? { id: paper.id } : undefined)}><I name="external" size={13}/> {t('exp.preview.open')}</button>
        <button className="btn sm" title="Save"><I name="bookmark" size={13}/></button>
        <button className="btn sm" title="Compare"><I name="compare" size={13}/></button>
      </div>
    </div>
  );
}

function EvalLine({ label, value, color }) {
  const v = Math.round(value * 100);
  return (
    <div className="col gap-2">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11.5, color: 'var(--fg-1)' }}>{label}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-0)' }}>{v}</span>
      </div>
      <ScoreBar value={value} color={color}/>
    </div>
  );
}

function ConnectLine({ project, reason }) {
  return (
    <div className="row gap-2" style={{ padding: '6px 0' }}>
      <div style={{ width: 4, height: 4, borderRadius: 999, background: 'var(--acc-indigo)', marginTop: 7, flexShrink: 0, boxShadow: '0 0 6px var(--acc-indigo)' }}/>
      <div className="col grow">
        <div style={{ fontSize: 11.5, color: 'var(--fg-0)' }}>{project}</div>
        <div style={{ fontSize: 11, color: 'var(--fg-2)' }}>{reason}</div>
      </div>
    </div>
  );
}

// styles
if (!document.getElementById('pp-explore-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-explore-styles';
  s.textContent = `
  .pp-filter-rail {
    width: 260px; flex-shrink: 0;
    border-right: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 18px 14px;
    overflow: auto;
    display: flex; flex-direction: column; gap: 18px;
  }
  .pp-filter-group { display: flex; flex-direction: column; }

  .pp-arxiv-searchbar {
    display: flex; gap: 8px; align-items: center;
    padding: 12px 14px 4px;
    flex-wrap: wrap;
  }
  .pp-arxiv-input-wrap {
    position: relative;
    flex: 1; min-width: 280px;
    display: flex; align-items: center; gap: 8px;
    padding: 0 12px;
    height: 34px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 8px;
  }
  .pp-arxiv-input-wrap:focus-within { border-color: var(--acc-indigo); }
  .pp-arxiv-input {
    flex: 1; height: 100%;
    background: transparent; border: none; outline: none;
    color: var(--fg-0);
    font-size: 13px;
  }
  .pp-arxiv-input::placeholder { color: var(--fg-3); }
  .pp-arxiv-spinner {
    width: 12px; height: 12px;
    border: 2px solid var(--border-1);
    border-top-color: var(--acc-indigo);
    border-radius: 50%;
    animation: pp-arxiv-spin 0.8s linear infinite;
  }
  @keyframes pp-arxiv-spin { to { transform: rotate(360deg); } }
  .pp-arxiv-days {
    height: 34px;
    padding: 0 10px;
    background: var(--bg-2);
    color: var(--fg-1);
    border: 1px solid var(--border-1);
    border-radius: 8px;
    font-size: 12px;
    cursor: pointer;
  }

  .pp-arxiv-card { cursor: pointer; }
  .pp-arxiv-card.is-added { border-color: var(--ok); box-shadow: 0 0 0 1px rgba(106,215,167,0.3); }
  .pp-arxiv-card-head { display: flex; gap: 10px; align-items: flex-start; }
  .pp-arxiv-check { padding-top: 2px; cursor: pointer; }
  .pp-arxiv-check input { width: 15px; height: 15px; accent-color: var(--acc-indigo); cursor: pointer; }
  .pp-arxiv-summary {
    margin-top: 8px;
    font-size: 12px; line-height: 1.55;
    color: var(--fg-2);
  }
  .pp-pill {
    height: 24px; padding: 0 8px;
    border-radius: 6px; border: 1px solid var(--border-1);
    background: var(--bg-2);
    color: var(--fg-2);
    font-family: var(--font-mono); font-size: 10.5px;
    cursor: pointer; transition: all .12s;
  }
  .pp-pill.is-on { background: rgba(136,150,255,0.12); color: var(--acc-indigo); border-color: rgba(136,150,255,0.40); }
  .pp-pill:hover { color: var(--fg-0); }

  .pp-seg {
    display: flex; padding: 3px; gap: 2px;
    border: 1px solid var(--border-1); background: var(--bg-2);
    border-radius: 6px;
  }
  .pp-seg-btn {
    flex: 1; padding: 5px 8px;
    border-radius: 4px;
    font-size: 11px; color: var(--fg-2);
    cursor: pointer; transition: all .12s;
  }
  .pp-seg-btn.is-on { background: rgba(136,150,255,0.14); color: var(--acc-indigo); box-shadow: 0 0 0 1px rgba(136,150,255,0.25); }

  .pp-check-row {
    display: flex; align-items: center; gap: 8px;
    padding: 5px 8px;
    border-radius: 5px;
    color: var(--fg-1);
    font-size: 12px;
    cursor: pointer;
  }
  .pp-check-row:hover { background: rgba(255,255,255,0.025); }
  .pp-check-row.is-on { color: var(--fg-0); }
  .pp-check-box {
    width: 14px; height: 14px; border-radius: 3px;
    border: 1px solid var(--border-2);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    color: #0B0D12;
  }
  .pp-check-row.is-on .pp-check-box {
    background: var(--acc-indigo);
    border-color: var(--acc-indigo);
    box-shadow: 0 0 8px rgba(136,150,255,0.40);
  }

  .pp-toggle-row {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 8px;
    color: var(--fg-1); font-size: 12px;
  }
  .pp-toggle {
    width: 26px; height: 14px; border-radius: 999px;
    background: rgba(255,255,255,0.08);
    position: relative; cursor: pointer;
    transition: background .15s;
  }
  .pp-toggle.is-on { background: var(--acc-indigo); box-shadow: 0 0 8px rgba(136,150,255,0.40); }
  .pp-toggle-knob {
    position: absolute; top: 2px; left: 2px;
    width: 10px; height: 10px; border-radius: 999px;
    background: var(--fg-1);
    transition: left .15s, background .15s;
  }
  .pp-toggle.is-on .pp-toggle-knob { left: 14px; background: #fff; }

  .pp-slider-track {
    height: 4px; background: rgba(255,255,255,0.06); border-radius: 999px;
    position: relative;
  }
  .pp-slider-fill {
    position: absolute; top: 0; bottom: 0;
    background: linear-gradient(90deg, var(--acc-indigo), var(--acc-violet));
    border-radius: 999px;
    box-shadow: 0 0 8px var(--acc-indigo);
  }
  .pp-slider-knob {
    position: absolute; top: 50%; transform: translate(-50%, -50%);
    width: 12px; height: 12px; border-radius: 999px;
    background: #fff;
    box-shadow: 0 0 0 2px var(--bg-0), 0 0 0 3px var(--acc-indigo), 0 0 12px var(--acc-indigo);
  }

  .pp-explore-center {
    flex: 1; min-width: 0;
    padding: 22px 26px 32px;
    overflow: auto;
    display: flex; flex-direction: column; gap: 18px;
  }
  .pp-explore-header {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
  }
  .pp-explore-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  }
  .pp-explore-card {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    padding: 14px;
    position: relative;
    transition: border-color .15s, background .15s;
    cursor: pointer;
  }
  .pp-explore-card:hover { border-color: var(--border-3); }
  .pp-explore-card.is-selected {
    border-color: rgba(136,150,255,0.50);
    box-shadow: 0 0 0 1px rgba(136,150,255,0.30), 0 0 30px rgba(136,150,255,0.10);
    background: linear-gradient(180deg, rgba(136,150,255,0.04), transparent);
  }
  .pp-explore-why {
    margin-top: 10px; padding: 8px 10px;
    background: rgba(180,155,255,0.06);
    border-left: 2px solid var(--acc-violet);
    border-radius: 0 5px 5px 0;
    display: flex; align-items: flex-start; gap: 6px;
  }
  .pp-compare-check {
    width: 16px; height: 16px; border-radius: 4px;
    border: 1.5px solid var(--border-3);
    display: flex; align-items: center; justify-content: center;
    background: var(--bg-3);
    color: #0B0D12;
  }
  .pp-explore-card.is-selected .pp-compare-check {
    background: var(--acc-indigo);
    border-color: var(--acc-indigo);
    box-shadow: 0 0 8px rgba(136,150,255,0.50);
  }
  .pp-explore-more {
    display: flex; justify-content: center; padding: 12px 0;
  }

  .pp-preview-rail {
    width: 360px; flex-shrink: 0;
    border-left: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 22px 18px 28px;
    overflow: auto;
  }

  .pp-mini-bench {
    padding: 4px 0;
  }
  `;
  document.head.appendChild(s);
}

export { ExploreScreen };
